import { useEffect, useRef, useState } from 'react';

/**
 * The Chat Overlay's live message stream (ARCHITECTURE.md §103.13).
 *
 * ==================== WHY A SOCKET AND NOT THE POLL ====================
 * Every other widget is happy re-reading a bootstrap every few seconds: a
 * goal's number changes rarely and being five seconds late costs nothing.
 * Chat is the opposite — it is the highest-volume thing Connaro carries, and
 * an overlay that showed each message up to one interval late, at the cost of
 * a database read per interval per open browser source, would be both slow
 * and expensive at the same time.
 *
 * So chat pushes. The bootstrap still paints the first frame after a reload
 * (§103.5); this takes over from there.
 * ======================================================================
 *
 * ==================== WHAT THIS DELIBERATELY DOES NOT DO ====================
 * No filtering. The server decides which platforms this widget receives and
 * applies the operator's ignore list BEFORE the wire (§103.13), because a
 * browser source is a public page and anything it was sent is disclosed
 * whether or not it was drawn. If a message arrives here, it was meant for
 * this overlay.
 * ===========================================================================
 */

export type LiveChatMessage = {
  id: string;
  platform: string;
  displayName: string | null;
  text: string | null;
  badges: { id: string; label?: string; version?: string }[];
  avatarUrl: string | null;
  occurredAt: string;
};

/** One message plus when THIS page first showed it. */
type Shown = {
  message: LiveChatMessage;
  /**
   * Client receipt time, not `occurredAt`.
   *
   * Lifetime means "how long it stays on screen", so the clock starts when it
   * appears. Expiring against `occurredAt` would make a reload wipe the entire
   * replay instantly — every replayed message is already older than any
   * sensible lifetime — and the overlay would come back blank after a scene
   * change, which is precisely the failure the replay exists to prevent.
   */
  shownAt: number;
};

function socketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws`;
}

/** Exponential backoff with full jitter, matching the Console's chat socket and §43. */
function backoffMs(attempt: number): number {
  const ceiling = Math.min(1_000 * 2 ** Math.min(attempt - 1, 5), 30_000);
  return Math.random() * ceiling;
}

export type WidgetChatOptions = {
  token: string | null;
  /** The bootstrap's replay. Re-seeds the window whenever it changes identity. */
  replay: readonly LiveChatMessage[];
  maxMessages: number;
  /** 0 means "stay until pushed off the end", which is the default. */
  lifetimeSeconds: number;
  /** False for every widget type that is not a Chat Overlay — no socket is opened. */
  active: boolean;
};

export function useWidgetChat(options: WidgetChatOptions): LiveChatMessage[] {
  const { token, replay, maxMessages, lifetimeSeconds, active } = options;

  /*
   * Seeded from the bootstrap replay SYNCHRONOUSLY, at mount, and never again.
   *
   * An effect would have been wrong twice over. It paints one empty frame
   * first — a visible flash of nothing on a scene change, where the replay
   * exists precisely so that does not happen. And the browser source keeps
   * polling the bootstrap so a configuration edit reaches a scene nobody will
   * refresh; each poll returns a fresh array, so an effect keyed on it would
   * delete every live message that had arrived since, clearing the overlay
   * every few seconds on air while the socket worked perfectly.
   *
   * This component is only mounted once the bootstrap has resolved, so the
   * replay is already here on the first render. After it, the socket is the
   * transport of record.
   */
  const [shown, setShown] = useState<Shown[]>(() => {
    const now = Date.now();
    return replay.map((message) => ({ message, shownAt: now }));
  });

  /*
   * The seen-set is a ref, not state.
   *
   * It must be consulted synchronously inside the socket handler, and a state
   * update would not be visible to the next message arriving in the same tick
   * — two deliveries of one message in quick succession would both pass.
   */
  const seenRef = useRef<Set<string>>(new Set(replay.map((message) => message.id)));

  useEffect(() => {
    if (!active || token === null || token === '') return undefined;

    let socket: WebSocket | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let disposed = false;

    const connect = (): void => {
      if (disposed) return;

      try {
        socket = new WebSocket(socketUrl());
      } catch {
        // Construction itself can throw on a malformed URL. Treat it as a
        // failed connection rather than letting it escape the effect.
        schedule();
        return;
      }

      socket.onopen = () => {
        attempt = 0;
        socket?.send(JSON.stringify({ v: 1, type: 'subscribe_widget', token }));
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        let frame: unknown;
        try {
          frame = JSON.parse(event.data);
        } catch {
          return;
        }
        if (typeof frame !== 'object' || frame === null || !('type' in frame)) return;

        const typed = frame as { type: string; message?: unknown; messageId?: unknown };

        if (typed.type === 'widget_chat') {
          const message = typed.message as LiveChatMessage | undefined;
          if (message === undefined || typeof message.id !== 'string') return;

          /*
           * Deduplicate on the PROVIDER's message id, which is what the
           * server projects into `id` when one exists (§103.13). A
           * redelivery — a reconnect replaying, a provider repeating itself —
           * is dropped; a message with no provider id is kept, because losing
           * a real message on a broadcast is worse than showing a duplicate.
           */
          if (seenRef.current.has(message.id)) return;
          seenRef.current.add(message.id);

          setShown((current) => {
            /*
             * APPENDED, never re-sorted (§103.13). Platforms deliver with
             * different latencies, so sorting by `occurredAt` would slide a
             * late message above lines a viewer has already read — the
             * overlay would appear to rewrite its own history mid-broadcast.
             */
            const next = [...current, { message, shownAt: Date.now() }];
            return next.length > maxMessages ? next.slice(-maxMessages) : next;
          });
          return;
        }

        if (typed.type === 'widget_chat_deleted') {
          const messageId = typed.messageId;
          if (typeof messageId !== 'string' || messageId === '') return;

          /*
           * Removed, and deliberately NOT struck through. The Console shows a
           * moderator what was removed; a broadcast showing it would leave the
           * content a moderator deleted on screen, which is the opposite of
           * what the deletion asked for.
           *
           * The id stays in the seen-set: a redelivery of a deleted message
           * must not put it back.
           */
          setShown((current) => current.filter((entry) => entry.message.id !== messageId));
        }
      };

      socket.onclose = () => {
        if (disposed) return;
        schedule();
      };

      socket.onerror = () => {
        // Always followed by `close`, which is where reconnection is decided.
      };
    };

    const schedule = (): void => {
      attempt += 1;
      retry = setTimeout(connect, backoffMs(attempt));
    };

    connect();

    return () => {
      disposed = true;
      if (retry !== undefined) clearTimeout(retry);
      /*
       * Handlers are detached before closing. `close()` fires `onclose`
       * synchronously in some browsers, and a live handler would schedule a
       * reconnect for a source that is being torn down — leaking a socket per
       * scene change for as long as OBS stayed open.
       */
      if (socket !== undefined) {
        socket.onclose = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onopen = null;
        socket.close();
      }
    };
  }, [token, active, maxMessages]);

  // Expiry. Only runs when a lifetime is configured; the default (0) keeps
  // messages until the window pushes them off, so there is no timer at all.
  useEffect(() => {
    if (lifetimeSeconds <= 0) return undefined;

    const timer = setInterval(() => {
      const cutoff = Date.now() - lifetimeSeconds * 1_000;
      setShown((current) => {
        const kept = current.filter((entry) => entry.shownAt > cutoff);
        // Same array when nothing expired, so React does not re-render the
        // overlay once a second for the life of the broadcast.
        return kept.length === current.length ? current : kept;
      });
    }, 1_000);

    return () => {
      clearInterval(timer);
    };
  }, [lifetimeSeconds]);

  return shown.map((entry) => entry.message);
}
